import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DnsRoundedIcon from "@mui/icons-material/DnsRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import RouterRoundedIcon from "@mui/icons-material/RouterRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { useMemo } from "react";
import { useNetworkStore } from "@/features/network-topology/store";
import type { EffectiveStatus, NodeKind } from "@/features/network-topology/types";

/**
 * React Flow のカスタムノード。
 *
 * 状態別ビジュアル:
 *  - online      : 緑枠、活気のあるアイコン背景
 *  - fault       : オレンジ枠 + box-shadow 点滅 (非停止のため flow は継続)
 *  - offline     : 薄グレー枠 + X 印
 *  - unreachable : 薄グレー枠 + X 印（半透明）
 *  - connecting  : 黄色枠（停止中・点滅なし）
 */

const KIND_ICON: Record<NodeKind, typeof HubRoundedIcon> = {
  internet: LanguageRoundedIcon,
  gateway: RouterRoundedIcon,
  l3_switch: HubRoundedIcon,
  l2_switch: HubRoundedIcon,
  server: DnsRoundedIcon,
  plc: MemoryRoundedIcon,
  robot_controller: SmartToyRoundedIcon,
  agv_controller: PrecisionManufacturingRoundedIcon,
  sensor: SensorsRoundedIcon,
};

const COLOR = {
  green: "#2ec4b6",
  greenSoft: "rgba(46,196,182,0.15)",
  orange: "#ff7a3d",
  orangeSoft: "rgba(255,122,61,0.15)",
  yellow: "#f6ae2d",
  faded: "#b0bec5",
} as const;

const blinkAnim = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 122, 61, 0.55); }
  50% { box-shadow: 0 0 0 8px rgba(255, 122, 61, 0); }
`;

function vizFor(status: EffectiveStatus): {
  border: string;
  iconBg: string;
  chipColor: "success" | "error" | "warning" | "default";
  chipLabel: string;
  faded: boolean;
  blink: boolean;
} {
  switch (status) {
    case "online":
      return {
        border: COLOR.green,
        iconBg: COLOR.greenSoft,
        chipColor: "success",
        chipLabel: "ONLINE",
        faded: false,
        blink: false,
      };
    case "fault":
      return {
        border: COLOR.orange,
        iconBg: COLOR.orangeSoft,
        chipColor: "warning",
        chipLabel: "FAULT",
        faded: false,
        blink: true,
      };
    case "offline":
      return {
        border: COLOR.faded,
        iconBg: "rgba(0,0,0,0.04)",
        chipColor: "default",
        chipLabel: "OFFLINE",
        faded: true,
        blink: false,
      };
    case "connecting":
      return {
        border: COLOR.yellow,
        iconBg: "rgba(246,174,45,0.15)",
        chipColor: "warning",
        chipLabel: "CONNECTING",
        faded: true,
        blink: false,
      };
    case "unreachable":
      return {
        border: COLOR.faded,
        iconBg: "rgba(0,0,0,0.04)",
        chipColor: "default",
        chipLabel: "UNREACHABLE",
        faded: true,
        blink: false,
      };
    default:
      return {
        border: COLOR.faded,
        iconBg: "rgba(0,0,0,0.04)",
        chipColor: "default",
        chipLabel: "—",
        faded: true,
        blink: false,
      };
  }
}

interface DeviceNodeData {
  networkNodeId: string;
}

export function DeviceNode({ data, selected }: NodeProps) {
  const { networkNodeId } = data as unknown as DeviceNodeData;
  const node = useNetworkStore((s) => s.nodes.find((n) => n.id === networkNodeId));
  const allNodes = useNetworkStore((s) => s.nodes);

  // effective を計算（メモ化）
  const status = useMemo<EffectiveStatus>(() => {
    if (!node) return "unreachable";
    if (node.manual === "offline") return "offline";
    if (node.manual === "connecting") return "connecting";
    let cur: string | null = node.parentId;
    while (cur !== null) {
      const p = allNodes.find((n) => n.id === cur);
      if (!p) return "unreachable";
      if (p.manual === "offline" || p.manual === "connecting") return "unreachable";
      cur = p.parentId;
    }
    return node.faulted ? "fault" : "online";
  }, [node, allNodes]);

  if (!node) return null;
  const Icon = KIND_ICON[node.kind];
  const viz = vizFor(status);
  const showXMark = status === "offline" || status === "unreachable";

  return (
    <Box
      sx={{
        width: 160,
        height: 84,
        position: "relative",
        background: "#ffffff",
        borderRadius: 2,
        border: "2px solid",
        // border は常に status 色を反映する（選択は outline で別レイヤに分離）
        borderColor: viz.border,
        // 選択視覚は青リングで status 色（緑/オレンジ）と衝突させない
        outline: selected ? "2px solid #1976d2" : "none",
        outlineOffset: selected ? 2 : 0,
        boxShadow: 1,
        px: 1,
        py: 0.75,
        opacity: viz.faded ? 0.55 : 1,
        transition: "border-color 160ms, opacity 160ms, outline-color 160ms",
        animation: viz.blink ? `${blinkAnim} 1.1s ease-in-out infinite` : "none",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ visibility: "hidden" }} />
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: node.simActive && status === "online" ? COLOR.greenSoft : viz.iconBg,
            color: status === "online" || status === "fault" ? viz.border : "text.secondary",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {node.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.1 }}>
            {node.kind.replace("_", " ")}
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ position: "absolute", bottom: 6, left: 8, right: 8, display: "flex", justifyContent: "center" }}>
        <Chip
          size="small"
          color={viz.chipColor}
          label={viz.chipLabel}
          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
        />
      </Box>
      {/* sim 連動マーカー */}
      {node.simBinding && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: node.simActive && (status === "online" || status === "fault") ? COLOR.green : "#cfd8dc",
            transition: "background 120ms",
          }}
          title={`sim binding: ${node.simBinding.kind}`}
        />
      )}
      {/* X 印（offline / unreachable） */}
      {showXMark && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 80, color: "rgba(120,120,120,0.45)" }} />
        </Box>
      )}
      <Handle type="source" position={Position.Bottom} style={{ visibility: "hidden" }} />
    </Box>
  );
}
